/**
 * NAV-5.7 — layout-matched first paint for Commission destinations.
 *
 * Reuses `@/components/ui/skeleton`. Kinds match the pages that exist: the
 * banded Overview (kind `health`, named for the card it replaced), periods
 * list, remounted list cards, Setup rail.
 */
import type { ReactNode } from 'react';

import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { COMMISSION_SETUP_GROUPS } from '@/lib/commissionAreaNav';
import type { CommissionSkeletonKind } from '@/lib/commissionDestinationSkeleton';
import { cn } from '@/lib/utils';

/** The Overview: a heading and summary line, then bands of rows. */
function OverviewBandsSkeleton() {
  return (
    <div className="space-y-7">
      <header className="space-y-2">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-4 w-full max-w-md" />
      </header>
      {[2, 3].map((rows, band) => (
        <div key={band} className="space-y-2">
          <Skeleton className="h-4 w-32" />
          <div className="divide-y rounded-lg border">
            {Array.from({ length: rows }, (_, i) => (
              <div
                key={i}
                className="flex items-center justify-between gap-4 px-4 py-3"
                style={{ minHeight: '4.5rem' }}
              >
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-3 w-28" />
                  <Skeleton className="h-4 w-full max-w-lg" />
                </div>
                <Skeleton className="h-8 w-32" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ListCardSkeleton() {
  return (
    <Card>
      <CardHeader className="space-y-2">
        <Skeleton className="h-6 w-44" />
        <Skeleton className="h-4 w-full max-w-lg" />
      </CardHeader>
      <CardContent>
        <div className="divide-y rounded-md border" style={{ minHeight: '16rem' }}>
          {Array.from({ length: 5 }, (_, i) => (
            <div key={i} className="flex items-center justify-between gap-3 p-3">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-4 w-24" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function PeriodsTableSkeleton() {
  return (
    <div className="space-y-4">
      <header className="space-y-2">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-4 w-full max-w-xl" />
      </header>
      {Array.from({ length: 3 }, (_, i) => (
        <Card key={i}>
          <CardHeader className="space-y-2 pb-3">
            <div className="flex items-center justify-between gap-2">
              <Skeleton className="h-5 w-56" />
              <Skeleton className="h-8 w-20" />
            </div>
            <Skeleton className="h-3 w-48" />
          </CardHeader>
          <CardContent className="flex flex-wrap gap-6" style={{ minHeight: '4.5rem' }}>
            <Skeleton className="h-10 w-24" />
            <Skeleton className="h-10 w-24" />
            <Skeleton className="h-10 w-24" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function SetupRailSkeleton() {
  return (
    <div className="flex min-h-[calc(100vh-4rem)]">
      <nav
        aria-hidden
        className="w-56 shrink-0 border-r border-border bg-muted/30 px-3 py-6"
      >
        {COMMISSION_SETUP_GROUPS.map((group) => (
          <div key={group} className="mb-4 space-y-2">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ))}
      </nav>
      <div className="min-w-0 flex-1 space-y-4 p-6">
        <ListCardSkeleton />
      </div>
    </div>
  );
}

function wrapPage(children: ReactNode, className?: string) {
  return (
    <div className={cn('mx-auto max-w-5xl space-y-4 p-6', className)}>{children}</div>
  );
}

export function CommissionDestinationSkeleton({
  kind,
  framed = true,
}: {
  kind: CommissionSkeletonKind;
  /**
   * When true (default), include the destination's page chrome so a Suspense
   * fallback occupies the same width as the loaded page. When false, render
   * only the inner card — used inside CommissionDestinationPage or a card
   * that is replacing its own spinner.
   */
  framed?: boolean;
}) {
  if (kind === 'rail') {
    return <SetupRailSkeleton />;
  }

  const inner =
    kind === 'health' ? (
      <OverviewBandsSkeleton />
    ) : kind === 'table' ? (
      <PeriodsTableSkeleton />
    ) : (
      <ListCardSkeleton />
    );

  return framed ? wrapPage(inner) : inner;
}
